/**
 * Cross-copy brand. `Symbol.for` reads from the runtime's global symbol registry, so two
 * loaded copies of @o2d/datasource resolve to the SAME symbol — which is what makes the check
 * survive a package that was not deduped. A string `.name` comparison would do that too, but
 * any error from anywhere carrying the same name would then be misread as a harmless
 * out-of-domain case and never reach monitoring. A registry key nobody else writes cannot
 * collide by accident.
 */
const VALUE_OUTSIDE_DOMAIN = Symbol.for('@o2d/datasource:ValueOutsideDomainError');

/**
 * Signals that a value does not exist in an axis' value domain at all.
 *
 * This is the EXPECTED failure of `AxisDataService.expandToValue()`: the caller asked for a
 * value the axis simply does not contain — an in-memory generator's own window limit, or an
 * entity that is not part of the axis' result set. It is a statement about the data, not a
 * malfunction.
 *
 * The distinction matters at the consuming end. `StandardDataProvider` drops the affected
 * entry either way, but it reports anything that is NOT this error to the configured
 * `CrosspointDataProviderErrorHandler` as well, so a systemic failure (backend unreachable,
 * timeouts, 5xx for every value) can reach monitoring instead of looking exactly like a single
 * harmless out-of-range date in the console.
 */

export class ValueOutsideDomainError extends Error {
    readonly [VALUE_OUTSIDE_DOMAIN] = true;

    /**
     * Recognises the error across package instances.
     *
     * `instanceof` compares class identity, so it fails if two copies of @o2d/datasource are
     * loaded at once — npm hoisting that did not dedupe, or a consumer bundling its own copy.
     * The failure would be silent and exactly inverted: every harmless out-of-domain value
     * would be reported as an outage, which is what this type exists to prevent.
     *
     * The brand is a registry symbol rather than the error's `.name`: both survive a second
     * copy, but a name is a string anyone can carry by coincidence, and this check decides
     * whether a failure is reported at all. A foreign error that happened to be named the same
     * would be swallowed as harmless — the exact silence this type was introduced to end.
     *
     * @param error - The rejection reason to classify
     * @returns true when it represents "the value is not in this axis' domain"
     */
    static is(error: unknown): boolean {
        return typeof error === 'object' && error !== null && VALUE_OUTSIDE_DOMAIN in error;
    }

    constructor(message: string) {
        super(message);
        this.name = 'ValueOutsideDomainError';

        // Insurance, not a repair: measured, this monorepo compiles to ES2022
        // (tsconfig.base.json), where subclassing Error keeps the prototype chain intact.
        // Down-levelling to ES5 would break it silently — `instanceof` would return false
        // with no compile error — and that check is the switch deciding whether a drop stays
        // local or is reported as an outage. One line to make the class survive a target
        // change nobody would connect to this behaviour.
        Object.setPrototypeOf(this, ValueOutsideDomainError.prototype);
    }
}
